import { Skeleton } from "@nexus/ui";
import {
  firstError,
  formatStatValue,
  HeaderStats,
  MetricValue,
  dataIssueKind,
  isExpectedDataIssue,
  WIDGET_ISSUE_TEXT,
  WidgetIssueState,
  unwrap,
  Widget,
} from "@nexus/widget-sdk";
import type {
  HeaderStat,
  HeaderStatFormat,
  WidgetDataRequirements,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";
import {
  EisBadge,
  eisColor,
  REPORT_METRICS_CONTRACT,
  type ReportMetric,
  type ReportMetricsData,
} from "@nexus/widget-kit";

export interface MetricSummaryEntry {
  /** Metric id in the collection. */
  id: string;
  /** Caption; defaults to the metric's own label. */
  label?: string;
  /** Draw this figure larger than the others (one headline figure per band). */
  emphasis?: boolean;
}

export interface MetricSummaryConfig {
  /** Heading inside the band; leave empty when the page or section already names it. */
  title?: string;
  /** One short paragraph under the heading, e.g. how the score is computed. */
  description?: string;
  /** Figures to show, in order. Empty shows every metric except the score. */
  metrics?: MetricSummaryEntry[];
  /** Metric drawn as the circular EIS gauge (0–100). */
  scoreMetricId?: string;
  /** Gauge caption "Avg EIS" rather than "EIS". */
  scoreIsAverage?: boolean;
  /** Caption of the score's sparkline, shown when the metric carries a `series`. */
  trendLabel?: string;
  /**
   * `card` (default): a framed band for the page body.
   * `inline`: frameless figures for a section header, where it stays visible
   * when the section is collapsed.
   */
  variant?: "card" | "inline";
  /**
   * The host titles the page with the collection's `subject` (e.g. the
   * campaign's name and dates); the band then shows no heading of its own.
   */
  pageHeading?: boolean;
  labels?: Record<string, string>;
}

export const manifest: WidgetManifest = {
  id: "metric-summary",
  name: "Metric summary",
  description:
    "Several figures of one metrics collection in a compact band: an EIS gauge with its trend and the key KPIs.",
  version: "1.0.0",
  dataRequests: {
    metrics: {
      source: "live-kpi-metrics",
      contract: REPORT_METRICS_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: MetricSummaryConfig = { variant: "card", scoreIsAverage: true };

function statFormat(metric: ReportMetric): HeaderStatFormat {
  if (metric.format === "percent") return "percent";
  if (metric.format === "decimal") return "decimal";
  return Math.abs(metric.value ?? 0) >= 10_000 ? "compact" : "number";
}

export function formatMetric(metric: ReportMetric | undefined): string {
  if (!metric) return "—";
  return formatStatValue(metric.value, statFormat(metric), metric.unit);
}

/** Minimal sparkline: widgets cannot import the host's chart wrappers. */
export function Sparkline({
  values,
  width = 88,
  height = 26,
  color = "currentColor",
}: {
  values: readonly number[];
  width?: number;
  height?: number;
  color?: string;
}) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const points = values
    .map(
      (value, index) =>
        `${(index * step).toFixed(1)},${(height - 2 - ((value - min) / span) * (height - 4)).toFixed(1)}`,
    )
    .join(" ");
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      className="overflow-visible"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Score({
  metric,
  isAverage,
  trendLabel,
  size,
}: {
  metric: ReportMetric | undefined;
  isAverage: boolean;
  trendLabel?: string;
  size: number;
}) {
  if (!metric) return null;
  const series = metric.series ?? [];
  return (
    <div className="flex shrink-0 items-center gap-3">
      <EisBadge score={metric.value} size={size} isAvg={isAverage} />
      {series.length >= 2 ? (
        <div className="flex flex-col items-center gap-1 text-center">
          <span className="whitespace-nowrap text-2xs font-medium text-muted">
            {trendLabel ?? "Trend"}
          </span>
          <span
            role="img"
            aria-label={`${trendLabel ?? "Trend"}: ${series.join(", ")}`}
            title={series.join(" → ")}
          >
            <Sparkline values={series} color={eisColor(series[series.length - 1])} />
          </span>
        </div>
      ) : null}
    </div>
  );
}

function pickMetrics(
  result: ReportMetricsData | null,
  config: MetricSummaryConfig,
): { entry: MetricSummaryEntry; metric: ReportMetric | undefined }[] {
  const metrics = result?.metrics ?? [];
  const entries =
    config.metrics && config.metrics.length > 0
      ? config.metrics
      : metrics
          .filter((metric) => metric.id !== config.scoreMetricId)
          .map((metric) => ({ id: metric.id }));
  return entries.map((entry) => ({
    entry,
    metric: metrics.find((metric) => metric.id === entry.id),
  }));
}

export function Component({ data, config }: WidgetProps<MetricSummaryConfig>) {
  const error = firstError(data, ["metrics"]);
  const result = unwrap<ReportMetricsData>(data.metrics);
  const loading = Boolean(data.metrics?.isLoading) && !result;
  const scoreMetric = config.scoreMetricId
    ? result?.metrics.find((metric) => metric.id === config.scoreMetricId)
    : undefined;
  const figures = pickMetrics(result, config);
  const isAverage = config.scoreIsAverage ?? true;
  const failure = error ? WIDGET_ISSUE_TEXT[dataIssueKind(error)] : null;

  if (config.variant === "inline") {
    const items: HeaderStat[] = figures.map(({ entry, metric }) => ({
      id: entry.id,
      label: entry.label?.trim() || metric?.label || entry.id,
      value: metric ? formatMetric(metric) : null,
      format: "text",
      loading,
    }));
    return (
      <div
        className="flex min-w-0 flex-wrap items-center justify-end gap-x-6 gap-y-2"
        aria-busy={loading || undefined}
      >
        {failure ? (
          // Compact form of the shared issue state, for a band inside a page header.
          <p
            className={`text-xs ${dataIssueKind(error) === "error" ? "text-status-error-text" : "text-secondary"}`}
            role="status"
            title={error && isExpectedDataIssue(error) ? error.message : undefined}
          >
            {failure}
          </p>
        ) : (
          <>
            <HeaderStats items={items} />
            <Score
              metric={scoreMetric}
              isAverage={isAverage}
              trendLabel={config.trendLabel}
              size={52}
            />
          </>
        )}
      </div>
    );
  }

  const title = config.pageHeading ? "" : (config.title?.trim() ?? "");
  return (
    // The heading stays in the body: the band shows the issue state beside it.
    <Widget title={title || manifest.name} hideHeader intrinsicHeight handlesDataIssue>
      <div
        className="flex flex-wrap items-center gap-x-6 gap-y-4 density-metric-padding"
        aria-busy={loading || undefined}
      >
        {!failure && (scoreMetric || loading) ? (
          <Score
            metric={scoreMetric}
            isAverage={isAverage}
            trendLabel={config.trendLabel}
            size={title ? 96 : 80}
          />
        ) : null}
        {title || config.description ? (
          <div className="min-w-0 flex-1 basis-80">
            {title ? (
              <h2 className="text-lg font-semibold text-brand-navy dark:text-primary">{title}</h2>
            ) : null}
            {config.description ? (
              <p className="mt-1 max-w-3xl text-sm leading-6 text-muted">{config.description}</p>
            ) : null}
          </div>
        ) : null}
        {error ? (
          <WidgetIssueState error={error} className="basis-full" />
        ) : (
          <dl className="grid w-full min-w-0 grid-cols-1 gap-x-6 gap-y-3 @sm/widget:grid-cols-2 @3xl/widget:ml-auto @3xl/widget:flex @3xl/widget:w-auto @3xl/widget:flex-wrap @3xl/widget:items-end @3xl/widget:justify-end">
            {figures.map(({ entry, metric }) => (
              <div key={entry.id} className="flex min-w-0 flex-col">
                <dt className="text-metric-caption font-medium text-secondary">
                  {entry.label?.trim() || metric?.label || entry.id}
                </dt>
                <dd className="mt-1">
                  {loading ? (
                    <Skeleton className={entry.emphasis ? "h-8 w-24" : "h-6 w-16"} />
                  ) : (
                    <MetricValue size={entry.emphasis ? "standard" : "compact"}>
                      {formatMetric(metric)}
                    </MetricValue>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </Widget>
  );
}

export function getDataRequirements(
  config: MetricSummaryConfig,
): Record<string, WidgetDataRequirements> {
  const score = config.scoreMetricId?.trim();
  return {
    metrics: {
      mode: "select",
      ...(config.metrics?.length
        ? {
            metricIds: [
              ...new Set([...config.metrics.map((entry) => entry.id), ...(score ? [score] : [])]),
            ],
          }
        : {}),
      historyMetricIds: score ? [score] : [],
      includeComparison: false,
      fields: ["metrics", "freshness", ...(config.pageHeading ? ["subject"] : [])],
    },
  };
}

const widgetModule: WidgetModule<MetricSummaryConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements,
};
export default widgetModule;
